import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { ApproveCounterSignRobosignTarPaperworkResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to approve counter-signing robotic session TAR paperwork.
 * Sets hasBeenCountersigned to true and completes the APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK action item.
 */
export const approveCounterSignRobosignTarPaperwork = async (
	servicesProviderId: string,
	orderId: string,
): Promise<ApproveCounterSignRobosignTarPaperworkResponse> => {
	const response = await apiClient.post<ApproveCounterSignRobosignTarPaperworkResponse>(
		ordersEndpoints.approveCounterSignRobosignTarPaperwork(servicesProviderId, orderId),
		{},
	);
	return response.data;
};

/**
 * Hook for approving counter-signing robotic session TAR paperwork.
 * This marks the counter-signing as complete and updates the action item status.
 *
 * @param orderId - The purchased vehicle ID
 */
export const useApproveCounterSignRobosignTarPaperwork = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId) {
				throw new Error('Unable to approve counter-signing paperwork without an order ID.');
			}
			if (!servicesProviderId) {
				throw new Error(
					'Unable to approve counter-signing paperwork without a services provider ID.',
				);
			}
			return approveCounterSignRobosignTarPaperwork(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK);
		},
	});
};
